import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { makeTitle } from "components/Dashboard/DoctorDashboard/notes-and-forms/utils/makeTitle";
import { listSchemasQueryOptions } from "queries/schemaQueryOptions";
import createNoteOrFormSchema from "services/createNoteOrFormSchema";
import updateNoteOrFormSchema from "services/updateNoteOrFormSchema";

const useSaveTemplatesMutation = () => {
  const queryClient = useQueryClient();

  const noteSchemasQuery = useQuery({
    ...listSchemasQueryOptions("note"),
    enabled: false,
  });
  const formSchemasQuery = useQuery({
    ...listSchemasQueryOptions("form"),
    enabled: false,
  });

  const saveTemplatesMutation = useMutation({
    mutationFn: async (payload: Partial<NoteOrFormTemplate>[]) => {
      const needsAllNoteSchemas = payload.some(
        (template) => template.type === "note",
      );
      const needsAllFormSchemas = payload.some(
        (template) => template.type === "form",
      );

      let noteSchemas = noteSchemasQuery.data;
      let formSchemas = formSchemasQuery.data;

      if (needsAllNoteSchemas && !noteSchemas) {
        noteSchemas = (await noteSchemasQuery.refetch()).data;
      }
      if (needsAllFormSchemas && !formSchemasQuery.data) {
        formSchemas = (await formSchemasQuery.refetch()).data;
      }

      return Promise.all(
        payload.map((template) => {
          switch (template.type) {
            case "note": {
              const noteAlreadyExists = !!template.id;

              const noteTitle = makeTitle({
                title: template.title ?? "Untitled Note",
                titles: (noteSchemas ?? [])
                  .filter(
                    (schema) =>
                      schema.note_json_schema.title &&
                      schema.note_schema_id !== template.id,
                  )
                  .map((schema) => schema.note_json_schema.title ?? ""),
              });

              const notePayload = {
                note_json_schema: {
                  title: noteTitle,
                },
                note_ui_schema: {},
                schema_v2: {
                  type: template.type,
                  title: noteTitle,
                  elements: template.elements ?? [],
                },
                note_schema_type: "progress_note" as NoteSchemaType,
              };

              if (noteAlreadyExists) {
                return updateNoteOrFormSchema(
                  "note",
                  template.id!,
                  notePayload,
                );
              }

              return createNoteOrFormSchema("note", notePayload);
            }

            case "form": {
              const formAlreadyExists = !!template.id;

              const formTitle = makeTitle({
                title: template.title ?? "Untitled Form",
                titles: (formSchemas ?? [])
                  .filter(
                    (schema) =>
                      schema.form_json_schema.title &&
                      schema.form_schema_id !== template.id,
                  )
                  .map((schema) => schema.form_json_schema.title ?? ""),
              });

              const formPayload = {
                form_json_schema: {
                  title: formTitle,
                },
                form_ui_schema: {},
                schema_v2: {
                  type: template.type,
                  title: formTitle,
                  elements: template.elements ?? [],
                },
              };

              if (formAlreadyExists) {
                return updateNoteOrFormSchema(
                  "form",
                  template.id!,
                  formPayload,
                );
              }

              return createNoteOrFormSchema("form", formPayload);
            }
            default:
              throw new Error(`Unknown template type: ${template.type}`);
          }
        }),
      );
    },
    onSettled: (_data, _error, variables) => {
      if (variables.some((template) => template.type === "note")) {
        queryClient.invalidateQueries({
          queryKey: ["note-schema"],
        });
      }
      if (variables.some((template) => template.type === "form")) {
        queryClient.invalidateQueries({
          queryKey: ["form-schema"],
        });
      }
    },
  });

  return saveTemplatesMutation;
};

export default useSaveTemplatesMutation;
